import React, { useState } from 'react';
import styled from 'styled-components';
import { useFormatter, useTranslations } from 'use-intl';
import { Button } from 'components';
import {
  QUARTER_HOUR_TIMES,
  browserTimezone,
  buildScheduledInstant,
  listTimezones,
  toZonedParts
} from 'utils';
import { SCHEDULE_DATE_TIME_FORMAT } from './ScheduleLine';

// Remembered for the life of the page so a batch of questions bound for the
// same slot is a run of quick confirmations rather than full entries.
let lastUsed = null;

const todayIn = (timezone) => toZonedParts(new Date(), timezone).date;

const initialEntry = ({ question, sessionTimezone }) => {
  const scheduledAt = question?.scheduledVisibleAt?.toDate?.();
  if (scheduledAt && question.scheduledVisibleTimezone) {
    return {
      ...toZonedParts(scheduledAt, question.scheduledVisibleTimezone),
      timezone: question.scheduledVisibleTimezone,
      notify: question.notifyOnReveal === true
    };
  }
  if (lastUsed) return lastUsed;
  const timezone = sessionTimezone || browserTimezone();
  // Emails are an explicit choice per schedule — never on by default, so a
  // test schedule can never mail a whole session by accident.
  return { date: todayIn(timezone), time: '12:00', timezone, notify: false };
};

function ScheduleRevealDialog({
  question,
  sessionTimezone,
  onConfirm,
  onDismiss,
  saving,
  error
}) {
  const t = useTranslations('Review');
  const tCommon = useTranslations('Common');
  const format = useFormatter();
  const [entry, setEntry] = useState(() =>
    initialEntry({ question, sessionTimezone })
  );
  const [localZone] = useState(browserTimezone);
  const [timezones] = useState(() => {
    const zones = listTimezones();
    const extras = [sessionTimezone, localZone, entry.timezone].filter(
      (zone) => zone && !zones.includes(zone)
    );
    return [...extras, ...zones];
  });

  const instant = buildScheduledInstant(entry);
  const isPast = Boolean(instant) && instant.getTime() <= Date.now();
  const canSave = Boolean(instant) && !isPast && !saving;
  const showLocalEquivalent = Boolean(instant) && entry.timezone !== localZone;

  const update = (field) => (event) =>
    setEntry((current) => ({ ...current, [field]: event.target.value }));

  const handleConfirm = () => {
    if (!canSave) return;
    lastUsed = entry;
    onConfirm({
      scheduledVisibleAt: instant,
      timezone: entry.timezone,
      notifyOnReveal: entry.notify
    });
  };

  return (
    <Container>
      <h2>{t('scheduleRevealTitle')}</h2>
      <Field>
        <label htmlFor="schedule-reveal-date">{t('scheduleDate')}</label>
        <input
          id="schedule-reveal-date"
          type="date"
          value={entry.date}
          onChange={update('date')}
          disabled={saving}
        />
      </Field>
      <Field>
        <label htmlFor="schedule-reveal-time">{t('scheduleTime')}</label>
        <select
          id="schedule-reveal-time"
          value={entry.time}
          onChange={update('time')}
          disabled={saving}>
          {QUARTER_HOUR_TIMES.map((time) => (
            <option key={time} value={time}>
              {time}
            </option>
          ))}
        </select>
      </Field>
      <Field>
        <label htmlFor="schedule-reveal-timezone">
          {t('scheduleTimezone')}
        </label>
        <select
          id="schedule-reveal-timezone"
          value={entry.timezone}
          onChange={update('timezone')}
          disabled={saving}>
          {timezones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </select>
      </Field>
      <CheckboxField>
        <input
          id="schedule-reveal-notify"
          type="checkbox"
          checked={entry.notify}
          onChange={(event) =>
            setEntry((current) => ({
              ...current,
              notify: event.target.checked
            }))
          }
          disabled={saving}
        />
        <label htmlFor="schedule-reveal-notify">{t('scheduleNotify')}</label>
      </CheckboxField>
      {isPast && <p className="error">{t('schedulePastError')}</p>}
      {showLocalEquivalent && !isPast && (
        <p className="local">
          {t('scheduleLocalEquivalent', {
            time: format.dateTime(instant, {
              ...SCHEDULE_DATE_TIME_FORMAT,
              timeZone: localZone
            }),
            timezone: localZone
          })}
        </p>
      )}
      {error && <p className="error">{error}</p>}
      <ButtonContainer>
        <Button onClick={onDismiss} disabled={saving}>
          {tCommon('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          loading={saving}
          disabled={!canSave}
          changeColorWhenDisabled>
          {tCommon('save')}
        </Button>
      </ButtonContainer>
    </Container>
  );
}

const Container = styled.section`
  background-color: #fff;
  border-radius: var(--border-radius-md);
  margin: 0 auto;
  max-width: 20.938rem;
  padding: 3.25rem 1.25rem 2.5rem;
  perspective: 50rem;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (min-width: 1200px) {
    max-width: 28.375rem;
    padding: 3.25rem 1.875rem 2.5rem;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1em;
    text-align: center;
    text-transform: uppercase;
  }

  p {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 140%;
    margin-bottom: 1.5rem;
    text-align: center;

    &.local {
      color: var(--light-brown);
    }

    &.error {
      color: var(--red);
      font-weight: 600;
    }
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.25rem;

  label {
    color: var(--dark-blue);
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
  }

  input,
  select {
    background: #fff;
    border: 0.063em solid var(--light-grey);
    border-radius: var(--border-radius-sm);
    color: var(--brown);
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    padding: 0.85em 1em;
    width: 100%;

    &:focus {
      background: rgba(226, 228, 234, 0.4);
      outline: 1px solid var(--blue);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

const CheckboxField = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1.5rem;

  input {
    accent-color: var(--dark-blue);
    flex-shrink: 0;
    height: 1.1rem;
    margin-top: 0.1rem;
    width: 1.1rem;
  }

  label {
    color: var(--dark-blue);
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 140%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 0.5rem;
  width: 100%;
`;

export default ScheduleRevealDialog;
